import { OriginatingTransactionPortfolioItem } from "../../../../../../types/amr-pipeline/models/OriginatingTransactionPortfolioItem";
import { concentrationPortfolioColumns } from "./concentration-columns";
import { ConcentrationTable } from "./ConcentrationTable";

interface Props {
    items?: OriginatingTransactionPortfolioItem[];
}

type Breakdown = Record<string, number>;

interface Statistics {
    moodysIndustryGroup: Breakdown;
    snpIndustryGroup: Breakdown;
    issuersGroup: Breakdown;
    countriesGroup: Breakdown;
    totalTargetPar: number;
}

const parseGroupToList = (group: Breakdown, totalSum: number) => {
    return Object.keys(group).map((name) => {
        const volume = group[name];
        return {
            name,
            volume,
            percentOfTotal: totalSum ? (volume / totalSum) * 100 : 0,
        };
    });
};

export const PortfolioConcentrations = ({ items = [] }: Props) => {
    const groupedStatistics = items.reduce<Statistics>(
        (accum, item) => {
            const {
                moodysIndustryGroup,
                snpIndustryGroup,
                issuersGroup,
                countriesGroup,
            } = accum;
            const { moodysIndustry, snpIndustry, issuer, country, targetPar } =
                item;

            if (moodysIndustry) {
                moodysIndustryGroup[moodysIndustry] =
                    (moodysIndustryGroup[moodysIndustry] || 0) + targetPar;
            }

            if (snpIndustry) {
                snpIndustryGroup[snpIndustry] =
                    (snpIndustryGroup[snpIndustry] || 0) + targetPar;
            }

            if (issuer) {
                issuersGroup[issuer] = (issuersGroup[issuer] || 0) + targetPar;
            }

            if (country) {
                countriesGroup[country] =
                    (countriesGroup[country] || 0) + targetPar;
            }

            accum.totalTargetPar += targetPar;
            return accum;
        },
        {
            moodysIndustryGroup: {},
            snpIndustryGroup: {},
            issuersGroup: {},
            countriesGroup: {},
            totalTargetPar: 0,
        }
    );

    const moodysIndustryList = parseGroupToList(
        groupedStatistics.moodysIndustryGroup,
        groupedStatistics.totalTargetPar
    );
    const snpIndustryList = parseGroupToList(
        groupedStatistics.snpIndustryGroup,
        groupedStatistics.totalTargetPar
    );
    const issuersList = parseGroupToList(
        groupedStatistics.issuersGroup,
        groupedStatistics.totalTargetPar
    );
    const countriesList = parseGroupToList(
        groupedStatistics.countriesGroup,
        groupedStatistics.totalTargetPar
    );

    return (
        <div className="row-items">
            <ConcentrationTable
                chartName="Moody’s Industry Concentrations, %"
                listItems={moodysIndustryList}
                columns={concentrationPortfolioColumns("Industry")}
            />
            <ConcentrationTable
                chartName="S&P Industry Concentrations, %"
                listItems={snpIndustryList}
                columns={concentrationPortfolioColumns("Industry")}
            />
            <ConcentrationTable
                chartName="Largest Issuers, %"
                listItems={issuersList}
                columns={concentrationPortfolioColumns("Issuer")}
            />
            <ConcentrationTable
                chartName="Issuer Geographic Concentration, %"
                listItems={countriesList}
                columns={concentrationPortfolioColumns("Country")}
            />
        </div>
    );
};
